import { createFileRoute } from "@tanstack/react-router";
import { loadCustomer } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

export const Route = createFileRoute("/customers/$slug")({
  loader: ({ params }) => loadCustomer({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.heading || loaderData.title,
          description: loaderData.seo_description || loaderData.description,
          image: loaderData.featured_image,
          url: `/customers/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/customers/${loaderData.slug}` }) : [],
  }),
  component: CustomerStory,
});

function CustomerStory() {
  const c = Route.useLoaderData();
  const quote = c.quote as
    | { copy?: string; author?: { name?: string; title?: string }; image?: string }
    | undefined;

  return (
    <div>
      {/* ── Hero ── */}
      <div className="relative overflow-hidden">
        <img
          src="/images/customer-story-hero-bg.png"
          alt=""
          className="pointer-events-none absolute right-0 top-0 h-full w-auto max-w-none opacity-30"
          loading="eager"
        />
        <div className="relative px-4 pb-14 pt-10 md:px-8 md:pb-20 md:pt-14 lg:pt-16">
          <div className="mx-auto max-w-screen-xl text-center">
            {/* C1 × Customer badge */}
            <div className="mb-6 inline-flex items-center gap-3 bg-teal-dark px-5 py-3">
              <img src="/images/svg/c1-ai-logo-white.svg" alt="C1" className="h-6 w-auto" />
              <span className="font-display text-lg font-bold text-white">×</span>
              {c.logo_light ? (
                <img src={c.logo_light} alt="" className="h-6 w-auto" loading="lazy" />
              ) : c.logo ? (
                <img src={c.logo} alt="" className="h-6 w-auto" loading="lazy" />
              ) : null}
            </div>
            <h1 className="mx-auto max-w-4xl font-display text-33 font-bold leading-tight text-charcoal md:text-40 lg:text-48">
              {c.heading || c.title}
            </h1>
          </div>
        </div>
      </div>

      {/* ── Video + Challenges & Results ── */}
      <div className="mx-auto max-w-screen-lg mx-auto px-4 py-10 md:px-8 lg:py-14 space-y-6">
        {/* Video */}
        {c.video_url && (
          <div className="relative aspect-video overflow-hidden rounded-lg">
            <iframe
              src={c.video_url}
              className="h-full w-full"
              allow="fullscreen"
              allowFullScreen
              style={{ border: "none" }}
              title={c.title}
            />
          </div>
        )}
        <div className="flex flex-col gap-10 md:flex-row md:gap-8 border border-gray-20">
          {c.challenges && c.challenges.length > 0 && (
            <div className="flex-1 p-6 md:p-8">
              <h2 className="chip mb-5 bg-charcoal text-white">
                Challenges
              </h2>
              <ul className="space-y-5">
                {c.challenges.map((ch, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-4 shrink-0">
                      <svg className="h-[14px] w-[14px]" viewBox="0 0 14 14" fill="none">
                        <path d="M12.432 12.57 1.573 2.02" stroke="#F85931" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.274 1.867 1.726 12.726" stroke="#F85931" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="font-body text-sm leading-snug text-gray-80">{ch}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.results && c.results.length > 0 && (
            <div className="flex-1 border-t border-gray-20 p-6 md:border-l md:border-t-0 md:p-8">
              <h2 className="chip mb-5 bg-teal-dark text-white">
                Results
              </h2>
              <ul className="space-y-5">
                {c.results.map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-0.5 w-4 shrink-0">
                      <svg className="h-5 w-auto" viewBox="0 0 23 24" fill="none">
                        <path d="M22 11.795c0 5.8-4.701 10.5-10.5 10.5S1 17.595 1 11.795s4.701-10.5 10.5-10.5S22 5.996 22 11.795Z" fill="#00343a" />
                        <path d="m7.021 12.762 3.577 2.958 5.688-6.664M22 11.795c0 5.8-4.701 10.5-10.5 10.5S1 17.595 1 11.795s4.701-10.5 10.5-10.5S22 5.996 22 11.795Z" stroke="#00343a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="m7.023 12.763 3.577 2.958 5.688-6.664" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="font-body text-sm leading-snug text-gray-80">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Quote — three-panel testimonial */}
        {quote?.copy && quote?.image && (
          <div className="mt-14 md:mt-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] overflow-hidden">
              {/* Left dither */}
              <div className="hidden lg:block">
                <img src="/images/testimonial-dither-left.png" alt="" className="h-full w-full object-cover" loading="lazy" />
              </div>
              {/* Center quote card */}
              <div className="relative bg-teal-dark p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                <p className="font-display text-xl font-bold leading-snug text-white md:text-23 lg:text-28">
                  &ldquo;{quote.copy}&rdquo;
                </p>
                {quote.author && (
                  <p className="mt-6 font-body text-15 font-semibold text-white">
                    {quote.author.name}
                    {quote.author.title && <>, {quote.author.title}</>}
                  </p>
                )}
                {c.logo_light && (
                  <img src={c.logo_light} alt="" className="mt-5 h-5 w-auto self-start" loading="lazy" />
                )}
              </div>
              {/* Right dither + portrait */}
              <div className="relative hidden lg:block">
                <img src="/images/testimonial-dither-right.png" alt="" className="h-full w-full object-cover" loading="lazy" />
                {quote.image && (
                  <img src={quote.image} alt={quote.author?.name || ""} className="absolute bottom-0 right-0 h-full w-auto object-cover object-bottom" loading="lazy" />
                )}
              </div>
              {/* Mobile portrait */}
              {quote.image && (
                <div className="relative bg-pink-accent lg:hidden">
                  <img src={quote.image} alt={quote.author?.name || ""} className="mx-auto h-48 w-auto object-cover object-top" loading="lazy" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lead text */}
        {c.lead && (
          <div className="mt-12 font-body text-2xl leading-relaxed text-charcoal sm:text-[1.53rem] sm:leading-relaxed md:mt-16">
            {c.lead}
          </div>
        )}

        {/* Body content */}
        {c.html && (
          <div
            className="content mt-16 font-body text-gray-80 md:mt-20"
            dangerouslySetInnerHTML={{ __html: c.html }}
          />
        )}
      </div>

      {/* ── About Section — two-panel ── */}
      {(c.about || c.industries || c.company_size || c.headquarters) && (
        <div className="mx-auto max-w-screen-lg px-4 pb-16 md:px-8 md:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 overflow-hidden">
            {/* Left: teal with logo + about */}
            <div className="bg-teal-dark p-8 md:p-10 lg:p-12">
              {(c.logo_light || c.logo) && (
                <img
                  src={c.logo_light || c.logo!}
                  alt=""
                  className="mb-6 h-8 w-auto"
                  loading="lazy"
                />
              )}
              {c.about && (
                <div
                  className="content font-body leading-relaxed text-white/80 [&_a]:text-white [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: c.about }}
                />
              )}
            </div>
            {/* Right: cream with metadata */}
            <div className="space-y-6 border border-gray-20 bg-[#F7F7F5] p-8 md:p-10 lg:p-12">
              {c.industries && c.industries.length > 0 && (
                <div>
                  <h3 className="font-display text-sm font-bold text-teal-dark">Industries</h3>
                  <p className="mt-1 font-body text-teal-dark">{c.industries.join(", ")}</p>
                </div>
              )}
              {c.company_size && (
                <div>
                  <h3 className="font-display text-sm font-bold text-teal-dark">Company Size</h3>
                  <p className="mt-1 font-body text-teal-dark">{c.company_size} employees</p>
                </div>
              )}
              {c.headquarters && (
                <div>
                  <h3 className="font-display text-sm font-bold text-teal-dark">Headquarters</h3>
                  <p className="mt-1 font-body text-teal-dark">{c.headquarters}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
